import { useCallback, useEffect, useState } from "react";

/**
 * Tab state synced to URL search params + browser history.
 *
 * Pushing tab changes into history means Android hardware back
 * (or browser back) navigates through tab history instead of
 * exiting the PWA / going to the previous page immediately.
 *
 * Usage:
 *   const [tab, setTab] = useHistoryTab("overview", "t");
 *
 * @param {string} initial - default tab value if none in URL
 * @param {string} key - search param key (default "tab")
 */
export const useHistoryTab = (initial, key = "tab") => {
  const readFromUrl = useCallback(() => {
    if (typeof window === "undefined") return initial;
    const sp = new URLSearchParams(window.location.search);
    return sp.get(key) || initial;
  }, [initial, key]);

  const [tab, setTabState] = useState(readFromUrl);

  useEffect(() => {
    const onPop = () => setTabState(readFromUrl());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [readFromUrl]);

  const setTab = useCallback(
    (next) => {
      const value = typeof next === "function" ? next(tab) : next;
      if (value === tab) return;
      const sp = new URLSearchParams(window.location.search);
      sp.set(key, value);
      const url = `${window.location.pathname}?${sp.toString()}${window.location.hash}`;
      window.history.pushState({ [key]: value }, "", url);
      setTabState(value);
    },
    [tab, key]
  );

  return [tab, setTab];
};
